// ────────────────────────────────────────────────────────────────────────────
//  auth.ts — Login / Logout / Current User
// ────────────────────────────────────────────────────────────────────────────

import { api, setToken, getToken, API_ORIGIN } from './api'

export interface AppUser {
  id:           string
  email:        string
  displayName:  string
  avatarUrl:    string | null
  accountType:  'Private' | 'Organisation'
  appRole:      'SuperAdmin' | 'Staff' | null
  orgId:        number | null
  orgRole:      string | null
  publicUsername: string | null
  emailConfirmed: boolean
}

interface AuthResponse {
  accessToken: string
  expiresIn:   number
  user:        AppUser
}

export async function loginWithEmail(email: string, password: string): Promise<AppUser> {
  const r = await api.post<AuthResponse>('/auth/login', { email, password }, { noAuth: true })
  setToken(r.accessToken)
  return r.user
}

export async function register(email: string, password: string, displayName: string): Promise<AppUser> {
  const r = await api.post<AuthResponse>('/auth/register', { email, password, displayName }, { noAuth: true })
  setToken(r.accessToken)
  return r.user
}

export async function fetchMe(): Promise<AppUser> {
  return api.get<AppUser>('/auth/me')
}

export function logout() {
  setToken(null)
}

// ── Profile-Updates ─────────────────────────────────────────────────────────

/// Vor- und Nachname ändern. Liefert den geupdated User.
export async function updateName(firstName: string, lastName: string): Promise<AppUser> {
  return api.put<AppUser>('/auth/me/name', { firstName, lastName })
}

/// Passwort ändern (braucht aktuelles Passwort als Bestätigung).
/// Worker: POST /auth/me/change-password { currentPassword, newPassword }
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  await api.post('/auth/me/change-password', { currentPassword, newPassword })
}

/// E-Mail ändern (Schritt 1: Code anfordern an die neue Adresse).
export async function requestEmailChange(newEmail: string): Promise<void> {
  await api.post('/auth/me/change-email/request', { newEmail })
}
/// E-Mail ändern (Schritt 2: Code bestätigen).
export async function confirmEmailChange(newEmail: string, code: string): Promise<AppUser> {
  const r = await api.post<{ success: boolean; user: AppUser }>('/auth/me/change-email/confirm', { newEmail, code })
  return r.user
}

/// Sprache (DE/EN) am Account speichern — beeinflusst Sprache der E-Mails.
export async function updateLanguage(language: 'de' | 'en'): Promise<void> {
  await api.put('/auth/me/language', { language })
}

/// Avatar hochladen (multipart). Liefert den geupdated User mit neuer avatarUrl.
/// Direkt-Call mit Bearer (Multipart geht nicht über den JSON-api-Helper).
export async function uploadAvatar(file: File): Promise<AppUser> {
  const fd = new FormData()
  fd.append('file', file)
  const res = await fetch(`${API_ORIGIN}/api/v1/auth/me/avatar`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${getToken()}` },
    body: fd,
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error ?? `HTTP ${res.status}`)
  return json as AppUser
}

export async function deleteAvatar(): Promise<AppUser> {
  return api.delete<AppUser>('/auth/me/avatar')
}
